import React from "react";
import { Copy, RefreshCw } from "lucide-react";

export const STATUS_COLOR = {
  pending: "text-amber-700 bg-amber-50",
  accepted: "text-[#064E3B] bg-[#064E3B]/10",
  expired: "text-[#A1A1AA] bg-[#F4F4F1]",
  cancelled: "text-red-600 bg-red-50",
  invited: "text-blue-700 bg-blue-50",
};

const StatusBadge = ({ status }) => (
  <span className={`text-xs px-2 py-1 rounded-sm font-medium ${STATUS_COLOR[status] || "text-[#52525B] bg-[#F4F4F1]"}`}>
    {status}
  </span>
);

export const InvitationsList = ({ invitations, onCopyLink, onResend }) => (
  <div className="space-y-3" data-testid="invitations-list">
    {invitations.length === 0 ? (
      <div className="card-flat text-center py-8 text-[#52525B] text-sm">No invitations sent yet.</div>
    ) : invitations.map((inv) => (
      <div key={inv.invitation_id} className="card-flat p-4" data-testid={`invitation-${inv.invitation_id}`}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-semibold text-sm text-[#0F172A]">{inv.name}</p>
            <p className="text-xs text-[#52525B]">{inv.email}</p>
            <p className="text-xs text-[#A1A1AA] mt-1">
              Expires: {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString("en-IN") : "N/A"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={inv.status} />
            {inv.status === "pending" && (
              <div className="flex gap-2">
                <button onClick={() => onCopyLink(inv.token)} className="text-xs flex items-center gap-1 text-[#52525B] hover:text-[#0F172A] border border-[#E4E4E1] px-2 py-1 rounded-sm" data-testid={`copy-link-${inv.invitation_id}`}>
                  <Copy size={11} /> Copy Link
                </button>
                <button onClick={() => onResend(inv.invitation_id)} className="text-xs flex items-center gap-1 text-[#52525B] hover:text-[#0F172A] border border-[#E4E4E1] px-2 py-1 rounded-sm">
                  <RefreshCw size={11} /> Renew
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    ))}
  </div>
);

export const NominationsList = ({ nominations, onConvert }) => (
  <div className="space-y-3" data-testid="nominations-list">
    {nominations.length === 0 ? (
      <div className="card-flat text-center py-8 text-[#52525B] text-sm">No nominations yet.</div>
    ) : nominations.map((nom) => (
      <div key={nom.nomination_id} className="card-flat p-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-semibold text-sm text-[#0F172A]">{nom.nominee_name}</p>
            <p className="text-xs text-[#52525B]">{nom.nominee_email}</p>
            <p className="text-xs text-[#A1A1AA] mt-1">Nominated by: {nom.nominator_name}</p>
            {nom.reason && <p className="text-xs text-[#52525B] mt-1 italic">&ldquo;{nom.reason}&rdquo;</p>}
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={nom.status} />
            {nom.status === "pending" && (
              <button onClick={() => onConvert(nom.nomination_id)} className="btn-primary text-xs py-1.5 px-3" data-testid={`convert-nomination-${nom.nomination_id}`}>
                Send Invitation
              </button>
            )}
          </div>
        </div>
      </div>
    ))}
  </div>
);

export const UsersList = ({ users, currentUserId, onRoleChange }) => (
  <div className="space-y-3" data-testid="users-list">
    {users.map((u) => (
      <div key={u.user_id} className="card-flat p-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <img src={u.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || "")}&background=0F172A&color=fff`} alt={u.name} className="w-9 h-9 rounded-full object-cover border border-[#E4E4E1]" />
            <div>
              <p className="font-semibold text-sm text-[#0F172A]">{u.name}</p>
              <p className="text-xs text-[#52525B]">{u.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs px-2 py-1 rounded-sm font-medium ${u.role === "admin" ? "bg-[#0F172A] text-white" : u.role === "council_member" ? "bg-[#064E3B]/10 text-[#064E3B]" : "bg-[#F4F4F1] text-[#52525B]"}`}>
              {u.role}
            </span>
            {u.user_id !== currentUserId && (
              <select value={u.role} onChange={(e) => onRoleChange(u.user_id, e.target.value)}
                className="text-xs border border-[#E4E4E1] px-2 py-1 rounded-sm bg-white focus:outline-none">
                <option value="explorer">Explorer</option>
                <option value="council_member">Council Member</option>
                <option value="admin">Admin</option>
              </select>
            )}
          </div>
        </div>
      </div>
    ))}
  </div>
);
